/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * BLOCO 12F — Calculador Autoritativo de Ajuste Proporcional e Regra dos 2 Dias
 * para Troca de Modelo Comercial (SPLIT, FIXED_MONTHLY, HYBRID).
 *
 * REGRAS DE NEGÓCIO:
 * 1. SPLIT atual: não há mensalidade prévia a prorratear; não gera cobrança retroativa.
 * 2. FIXED_MONTHLY ou HYBRID atual:
 *    - Regra dos 2 Dias: Se o ciclo está pago/regular, sem inadimplência e a troca ocorre
 *      nos 2 primeiros dias do ciclo, custo proporcional = R$ 0,00 (NO_CHARGE).
 *    - Fora dos 2 dias (ou se inadimplente): calcula valor proporcional exato para os
 *      dias restantes do ciclo no novo modelo com base no calendário real do mês (28, 29, 30, 31 dias).
 * 3. HYBRID: calcula proporcional apenas sobre a parcela fixa mensal; comissão por reserva é independente.
 * 4. Arredondamento determinístico em centavos (2 casas decimais).
 * 5. Idempotência estrita: sem mutações ou criação de registros colaterais.
 */

import { ArenaFinancialModelType } from "../../types/commercialRules";

export type ModelChangeDecision = "NO_CHARGE" | "PRORATED_CHARGE";

export interface ModelChangeProportionalityParams {
  currentModel: ArenaFinancialModelType;
  targetModel: ArenaFinancialModelType;
  currentMonthlyFee?: number;
  targetMonthlyFee?: number;
  monthlyValue?: number; // Alias retrocompatível
  cycleStartDateIso?: string | null;
  cycleDueDateIso?: string | null;
  startDateIso?: string | null; // Alias retrocompatível
  nextDueDateIso?: string | null; // Alias retrocompatível
  lastPaymentDateIso?: string | null;
  subscriptionStatus?: string | null;
  cycleStatus?: string | null;
  isOverdue?: boolean;
  requestDate?: Date | string;
}

export interface ModelChangeProportionalityResult {
  currentModel: ArenaFinancialModelType;
  targetModel: ArenaFinancialModelType;
  cycleStartDate: string;
  cycleDueDate: string;
  requestDate: string;
  totalCycleDays: number;
  daysElapsed: number;
  remainingDays: number;
  dailyRate: number;
  currentMonthlyFee: number;
  targetMonthlyFee: number;
  isWithin2Days: boolean;
  isPaidOrRegular: boolean;
  isDelinquent: boolean;
  decision: ModelChangeDecision;
  proratedAmount: number;
  chargeRequired: boolean;
  reason: string;

  // Campos de retrocompatibilidade com ProportionalCalculationResult
  cycleEndDate: string;
  periodConsidered: string;
  monthlyValue: number;
  daysConsidered: number;
  proratedValue: number;
  amountPaid: number;
  remainingBalanceDue: number;
  requiresPayment: boolean;
  summaryMessage: string;
  requestedModel?: ArenaFinancialModelType;
}

export type ProportionalCalculationResult = ModelChangeProportionalityResult;

/**
 * Calcula a quantidade exata de dias em um ciclo comercial respeitando o calendário real (28, 29, 30, 31 dias).
 */
export function calculateCycleTotalDays(startDate: Date, dueDate: Date): number {
  const diffMs = dueDate.getTime() - startDate.getTime();
  const rawDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // Se startDate for o 1º dia do mês e dueDate for o último dia do mesmo mês (ex: 01/06 a 30/06 = 29 dias via diffMs),
  // o ciclo compreende o mês cheio (30 dias).
  const startDay = startDate.getUTCDate();
  const dueDay = dueDate.getUTCDate();
  const daysInStartMonth = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, 0)).getUTCDate();

  if (startDay === 1 && dueDay === daysInStartMonth && startDate.getUTCMonth() === dueDate.getUTCMonth()) {
    return daysInStartMonth;
  }

  return Math.max(1, rawDays);
}

/**
 * Normaliza uma data ISO ou Date para UTC meia-noite.
 */
function normalizeToUtcMidnight(input?: Date | string | null, fallback?: Date): Date {
  if (!input) return fallback ? new Date(Date.UTC(fallback.getUTCFullYear(), fallback.getUTCMonth(), fallback.getUTCDate())) : new Date();
  
  if (typeof input === "string") {
    const clean = input.split("T")[0];
    const parts = clean.split("-").map(Number);
    if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
      return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    }
    const d = new Date(input);
    return isNaN(d.getTime()) ? (fallback || new Date()) : new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  }

  return new Date(Date.UTC(input.getUTCFullYear(), input.getUTCMonth(), input.getUTCDate()));
}

export class ProportionalFeeCalculator {
  /**
   * BLOCO 12F — Calcula a proporcionalidade financeira e decide se há cobrança na troca de modelo.
   */
  static calculateModelChangeFinancials(
    params: ModelChangeProportionalityParams
  ): ModelChangeProportionalityResult {
    const {
      currentModel,
      targetModel,
      currentMonthlyFee = params.monthlyValue || 0,
      targetMonthlyFee = params.monthlyValue || 0,
      cycleStartDateIso = params.startDateIso,
      cycleDueDateIso = params.nextDueDateIso,
      requestDate = new Date(),
    } = params;

    const reqDate = normalizeToUtcMidnight(requestDate);
    const reqIso = reqDate.toISOString().split("T")[0];

    let cycleStart = reqDate;
    let cycleDue = new Date(Date.UTC(reqDate.getUTCFullYear(), reqDate.getUTCMonth() + 1, reqDate.getUTCDate()));

    if (cycleStartDateIso && cycleDueDateIso) {
      cycleStart = normalizeToUtcMidnight(cycleStartDateIso);
      cycleDue = normalizeToUtcMidnight(cycleDueDateIso);
    }

    const startIso = cycleStart.toISOString().split("T")[0];
    const dueIso = cycleDue.toISOString().split("T")[0];
    const totalCycleDays = calculateCycleTotalDays(cycleStart, cycleDue);

    let proratedAmount = 0;
    if (targetModel === "FIXED_MONTHLY" || targetModel === "HYBRID") {
      // Sempre cobra o valor INTEGRAL da nova modalidade comercial de destino
      proratedAmount = targetMonthlyFee;
    }

    const decision: ModelChangeDecision = proratedAmount > 0 ? "PRORATED_CHARGE" : "NO_CHARGE";
    const reason = targetModel === "SPLIT"
      ? "Transição para o modelo gratuito SPLIT. Sem cobrança adicional."
      : `Transição para o modelo ${targetModel}. Cobrança pelo valor integral da nova contratação (R$ ${targetMonthlyFee.toFixed(2)}).`;

    return {
      currentModel,
      targetModel,
      requestedModel: targetModel,
      cycleStartDate: startIso,
      cycleDueDate: dueIso,
      requestDate: reqIso,
      totalCycleDays,
      daysElapsed: 0,
      remainingDays: totalCycleDays,
      dailyRate: totalCycleDays > 0 ? targetMonthlyFee / totalCycleDays : 0,
      currentMonthlyFee,
      targetMonthlyFee,
      isWithin2Days: false,
      isPaidOrRegular: true,
      isDelinquent: false,
      decision,
      proratedAmount,
      chargeRequired: proratedAmount > 0,
      reason,
      cycleEndDate: dueIso,
      periodConsidered: `${startIso} a ${dueIso}`,
      monthlyValue: targetMonthlyFee,
      daysConsidered: totalCycleDays,
      proratedValue: proratedAmount,
      amountPaid: 0,
      remainingBalanceDue: proratedAmount,
      requiresPayment: proratedAmount > 0,
      summaryMessage: reason,
    };
  }

  /**
   * Método de regularização retroativa / proporcional para cálculo de saldo pendente anterior (usado na suíte histórica).
   */
  static calculateProportionalBalance(params: {
    currentModel: ArenaFinancialModelType;
    requestedModel: ArenaFinancialModelType;
    monthlyValue?: number;
    startDateIso?: string | null;
    nextDueDateIso?: string | null;
    lastPaymentDateIso?: string | null;
    subscriptionStatus?: string | null;
    requestDate?: Date;
  }): ModelChangeProportionalityResult {
    const {
      currentModel,
      requestedModel,
      monthlyValue = 0,
      startDateIso,
      nextDueDateIso,
      lastPaymentDateIso,
      subscriptionStatus,
      requestDate = new Date(),
    } = params;

    const reqTime = requestDate.getTime();
    const todayStr = requestDate.toISOString().split("T")[0];

    // 1. SPLIT não possui mensalidade prévia
    if (currentModel === "SPLIT" || monthlyValue <= 0) {
      return {
        currentModel,
        targetModel: requestedModel,
        requestedModel,
        cycleStartDate: todayStr,
        cycleDueDate: todayStr,
        requestDate: todayStr,
        totalCycleDays: 30,
        daysElapsed: 0,
        remainingDays: 0,
        dailyRate: 0,
        currentMonthlyFee: 0,
        targetMonthlyFee: 0,
        isWithin2Days: false,
        isPaidOrRegular: true,
        isDelinquent: false,
        decision: "NO_CHARGE",
        proratedAmount: 0,
        chargeRequired: false,
        reason: "Modelo SPLIT não possui mensalidade fixa. Sem saldo devedor pendente.",
        cycleEndDate: todayStr,
        periodConsidered: `${todayStr} a ${todayStr}`,
        monthlyValue: 0,
        daysConsidered: 0,
        proratedValue: 0,
        amountPaid: 0,
        remainingBalanceDue: 0,
        requiresPayment: false,
        summaryMessage: "Modelo SPLIT não possui mensalidade fixa. Sem saldo devedor pendente.",
      };
    }

    // 2. Determina as datas do ciclo
    let cycleStart: Date;
    let cycleEnd: Date;

    if (nextDueDateIso) {
      const cleanDue = nextDueDateIso.split("T")[0];
      cycleEnd = new Date(`${cleanDue}T00:00:00.000Z`);
      if (startDateIso) {
        const cleanStart = startDateIso.split("T")[0];
        cycleStart = new Date(`${cleanStart}T00:00:00.000Z`);
        if (cycleStart.getTime() >= cycleEnd.getTime()) {
          cycleStart = new Date(cycleEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
        }
      } else {
        cycleStart = new Date(cycleEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
      }
    } else if (startDateIso) {
      const cleanStart = startDateIso.split("T")[0];
      cycleStart = new Date(`${cleanStart}T00:00:00.000Z`);
      cycleEnd = new Date(cycleStart.getTime() + 30 * 24 * 60 * 60 * 1000);
    } else {
      cycleStart = new Date(reqTime - 15 * 24 * 60 * 60 * 1000);
      cycleEnd = new Date(reqTime + 15 * 24 * 60 * 60 * 1000);
    }

    const startIso = cycleStart.toISOString().split("T")[0];
    const endIso = cycleEnd.toISOString().split("T")[0];
    const reqIso = requestDate.toISOString().split("T")[0];

    const diffMs = cycleEnd.getTime() - cycleStart.getTime();
    const totalCycleDays = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)));

    let daysConsidered = 0;
    if (reqTime <= cycleStart.getTime()) {
      daysConsidered = 0;
    } else if (reqTime >= cycleEnd.getTime()) {
      daysConsidered = totalCycleDays;
    } else {
      const elapsedMs = reqTime - cycleStart.getTime();
      daysConsidered = Math.max(1, Math.min(totalCycleDays, Math.floor(elapsedMs / (1000 * 60 * 60 * 24)) + 1));
    }

    const proratedValue = Math.round(((daysConsidered / totalCycleDays) * monthlyValue) * 100) / 100;

    let isFullyPaid = false;
    if (subscriptionStatus === "ACTIVE") {
      if (lastPaymentDateIso) {
        const lastPayTime = new Date(lastPaymentDateIso).getTime();
        if (lastPayTime >= cycleStart.getTime() - 2 * 24 * 60 * 60 * 1000) {
          isFullyPaid = true;
        }
      } else {
        isFullyPaid = true;
      }
    }

    const amountPaid = isFullyPaid ? monthlyValue : 0;
    const remainingBalanceDue = isFullyPaid ? 0 : Math.max(0, Math.round((proratedValue - amountPaid) * 100) / 100);
    const requiresPayment = remainingBalanceDue > 0;
    const periodConsidered = `${startIso} a ${reqIso}`;

    let summaryMessage = "";
    if (isFullyPaid) {
      summaryMessage = `Ciclo atual (${periodConsidered}) já se encontra quitado (R$ ${monthlyValue.toFixed(2)}). Nenhuma cobrança adicional devida para a troca de modelo.`;
    } else if (requiresPayment) {
      summaryMessage = `Ajuste proporcional de R$ ${remainingBalanceDue.toFixed(2)} referente a ${daysConsidered} dia(s) de uso no modelo ${currentModel} (${periodConsidered}).`;
    } else {
      summaryMessage = `Nenhum saldo devedor pendente para a troca do modelo ${currentModel} para ${requestedModel}.`;
    }

    return {
      currentModel,
      targetModel: requestedModel,
      requestedModel,
      cycleStartDate: startIso,
      cycleDueDate: endIso,
      requestDate: reqIso,
      totalCycleDays,
      daysElapsed: daysConsidered,
      remainingDays: Math.max(0, totalCycleDays - daysConsidered),
      dailyRate: Math.round((monthlyValue / totalCycleDays) * 10000) / 10000,
      currentMonthlyFee: monthlyValue,
      targetMonthlyFee: 0,
      isWithin2Days: daysConsidered <= 2 && isFullyPaid,
      isPaidOrRegular: isFullyPaid,
      isDelinquent: subscriptionStatus === "SUSPENDED" || subscriptionStatus === "OVERDUE",
      decision: requiresPayment ? "PRORATED_CHARGE" : "NO_CHARGE",
      proratedAmount: remainingBalanceDue,
      chargeRequired: requiresPayment,
      reason: summaryMessage,
      cycleEndDate: endIso,
      periodConsidered,
      monthlyValue,
      daysConsidered,
      proratedValue,
      amountPaid,
      remainingBalanceDue,
      requiresPayment,
      summaryMessage,
    };
  }
}
